<template>
  <section class="request_management__content">
    <header class="content__header">
      <h2 v-html="$t('userInfo.lnb.requestManagement')"></h2>

      <div class="input__search">
        <input type="text"
          class="search-input"
          :value="searchKeyword"
          @input="searchKeyword = $event.target.value.trim().toLowerCase()">
        <i class="icon icon--search"></i>
      </div>
    </header>

    <div v-if="hasResult"
      class="content__body" ref="scrollContent">
      <content-box>
        <table>
          <colgroup>
            <col width="128px">
            <col width="128px">
            <col width="*">
            <col width="178px">
            <col width="165px">
          </colgroup>
          <thead class="item__header">
            <tr>
              <th class="align-left data__page">
                <sort-options
                  columnName="userId"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange">{{ $t('common.id') }}</sort-options>
              </th>
              <th class="align-left data__page">
                <sort-options
                  columnName="branchName"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange">{{ $t('common.corporation') }}</sort-options>
              </th>
              <th class="align-left ellipsis data__page">
                {{ $t('common.countryCode') }}
              </th>
              <th class="align-left data__page">
                <sort-options
                  columnName="email"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange">{{ $t('common.email') }}</sort-options>
              </th>
              <th class="align-left"></th>
            </tr>
          </thead>
          <tbody class="item__body" v-if="userList && userList.length">
            <tr v-for="user of userList" :key="user.userNum" v-show="!user.isHide">
              <td class="align-left ellipsis">
                <text-highlight :searchKeyword="searchKeyword"
                  :textItem="user.userId"></text-highlight>
              </td>
              <td class="align-left ellipsis">
                <text-highlight :searchKeyword="searchKeyword"
                  :textItem="user.branchName"></text-highlight>
              </td>
              <td class="align-left ellipsis">
                <text-highlight :searchKeyword="searchKeyword" style="min-width: 200px"
                  :textItem="user.siteCodeList.join(', ')"></text-highlight>
              </td>
              <td class="align-left ellipsis" @click="copyEmail(user.email)">
                <text-highlight :searchKeyword="searchKeyword"
                  :textItem="user.email"></text-highlight>
              </td>
              <td class="align-right data__button">
                <ui-button :primary1="true" :size="'small'" @click="onRejectConfirm(user)">
                  {{ $t('userInfo.requestManagement.reject.button') }}
                </ui-button>
                <ui-button :primary="true" :size="'small'" @click="onApproveConfirm(user)">
                  {{ $t('userInfo.requestManagement.approve.button') }}
                </ui-button>
              </td>
            </tr>
          </tbody>
          <tbody class="item__body" v-else-if="userList && !userList.length">
            <tr>
              <td colspan="5" class="no-request">
                {{ $t('userInfo.requestManagement.noRequestFound') }}
              </td>
            </tr>
          </tbody>
        </table>

        <div class="load" v-if="!userList">
          <ui-loading-circle>
            {{ $t('common.loading') }}
          </ui-loading-circle>
        </div>
      </content-box>

      <back-to-top :refName="'scrollContent'" />
    </div>

    <div v-else class="content__body">
      <div class="no-result">
        <i class="icon icon--empty"></i>
        <p class="title">{{ $t('dashboard.noApplies.searchResult.title') }}</p>
        <p>{{ $t('dashboard.noApplies.searchResult.desc') }}</p>
        <p class="keyword">'{{ searchKeyword }}'</p>
      </div>
    </div>

    <dimmed v-if="userToReject">
      <content-box class="user_reject" :width="'430px'">
        <span class="icon icon--alert"></span>
        <p class="title">{{ $t('userInfo.requestManagement.reject.title') }}</p>
        <p class="desc">
          {{ $t('userInfo.requestManagement.reject.desc.front') }}
          <span>{{ userToReject.userId }}</span>
          {{ $t('userInfo.requestManagement.reject.desc.back') }}
        </p>
        <ui-button
          @click="userToReject = null">{{ $t('common.cancel') }}</ui-button>
        <ui-button :primary2="true"
          @click="onRejectUser">{{ $t('common.confirm') }}</ui-button>
      </content-box>
    </dimmed>

    <dimmed v-if="userToApprove">
      <content-box class="user_approve" :width="'830px'" :height="'543px'">
        <button class="close" @click="userToApprove = null">
          <i class="icon icon--close14"></i>
        </button>
        <div v-if="approveStep === 1">
          <h3 v-html="$t('userInfo.requestManagement.approve.step1.title')"></h3>
          <div class="select">
            <div class="half">
              <span class="icon icon--localmanager"></span>
              <p class="title">{{ $t('userInfo.requestManagement.approve.step1.localManager.title') }}</p>
              <p class="desc">{{ $t('userInfo.requestManagement.approve.step1.localManager.desc') }}</p>
              <ui-button
                :primary2="true"
                @click="onApproveUserStep2(true)">{{ $t('common.next') }}</ui-button>
            </div>
            <div class="half">
              <span class="icon icon--viewer"></span>
              <p class="title">{{ $t('userInfo.requestManagement.approve.step1.viewer.title') }}</p>
              <p class="desc">{{ $t('userInfo.requestManagement.approve.step1.viewer.desc') }}</p>
              <ui-button
                :primary2="true"
                @click="onApproveUserStep2(false)">{{ $t('common.next') }}</ui-button>
            </div>
          </div>
        </div>
        <div v-else-if="approveStep === 2">
          <h3 v-html="$t('userInfo.requestManagement.approve.step2.title')"></h3>
          <country-code-selectbox
            :value="model.originalSiteCodeList"
            :width="'770px'"
            :height="'353px'"
            :full="true"
            @change="onSiteCodeChange"/>
          <div class="buttons">
            <ui-button
              :primary1="true"
              @click="approveStep = 1">{{ $t('common.back') }}</ui-button>
            <ui-button
              :primary2="true"
              :disabled="!canGoStep3"
              @click="approveStep = 3">{{ $t('common.next') }}</ui-button>
          </div>
        </div>
        <div v-else-if="approveStep === 3">
          <h3 v-html="$t('userInfo.requestManagement.approve.step3.title')"></h3>
          <ui-check-selectbox
            id="signUpTimezone"
            :value="model.originalSiteCodeList.includes('ALL') ? allEtcSiteCodeList : model.etcSiteCodeList"
            :width="'770px'"
            :height="'353px'"
            :full="true"
            :unSelectAll="true"
            :disableCheckbox="model.originalSiteCodeList.includes('ALL')"
            :search="false"
            :maxHeight="321"
            :items="etcSiteCodeList"
            @input="onEtcSiteCodeChange"/>
          <div class="buttons">
            <ui-button
              :primary1="true"
              @click="approveStep = 2">{{ $t('common.back') }}</ui-button>
            <ui-button
              :primary2="true"
              @click="onApproveUser">{{ $t('common.apply') }}</ui-button>
          </div>
        </div>
      </content-box>
    </dimmed>
    <transition name="fade">
      <div v-show="!!toastMessage"
           class="layout-toast"
           v-html="realToastMessage">
      </div>
    </transition>
  </section>
</template>

<script>
import ContentBox from '@/components/ui/ContentBox'
import BackToTop from '@/components/ui/BackToTop'
import SortOptions from '@/components/ui/SortOptions'
import TextHighlight from '@/components/ui/TextHighlight'
import Dimmed from '@/components/ui/Dimmed'
import UiButton from '@/components/ui/UiButton'
import UiCheckSelectbox from '@/components/ui/UiCheckSelectbox'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle.vue'
import CountryCodeSelectbox from '@/components/ui/CountryCodeSelectbox'
import { userList, userApprove } from '@/api/user'
import { getEtcSiteCodeList } from '@/api/apply'

export default {
  components: {
    ContentBox,
    SortOptions,
    TextHighlight,
    BackToTop,
    UiButton,
    UiCheckSelectbox,
    UiLoadingCircle,
    CountryCodeSelectbox,
    Dimmed
  },
  data () {
    return {
      searchKeyword: '',
      hasResult: true,
      userList: null,
      model: {
        updatePermission: null,
        originalSiteCodeList: null,
        etcSiteCodeList: null
      },
      sortOptions: {
        column: 'userId',
        isAsc: true
      },
      userToReject: null,
      userToApprove: null,
      approveStep: 1,
      wasAllSelected: false,
      canGoStep3: true,
      allEtcSiteCodeList: [],
      etcSiteCodeList: [{
        value: '',
        text: '',
        items: []
      }],
      toastMessage: null,
      realToastMessage: null
    }
  },
  computed: {
    sortColumn () {
      return this.sortOptions.column
    },
    isSortAsc () {
      return this.sortOptions.isAsc
    }
  },
  watch: {
    searchKeyword (value) {
      if (!value.length) {
        this.hasResult = true
      } else {
        this.hasResult = false
      }

      this.userList.forEach(item => {
        item.isHide = false
        if (value.length) {
          if (!item.userId.toLowerCase().includes(value) &&
            !item.branchName.toLowerCase().includes(value) &&
            !item.siteCodeList.join().includes(value.toUpperCase()) &&
            !item.email.toLowerCase().includes(value)
          ) {
            item.isHide = true
          } else {
            this.hasResult = true
          }
        }
      })
    },
    userToApprove (value) {
      if (value) {
        this.model.updatePermission = value.updatePermission
        this.model.originalSiteCodeList = value.originalSiteCodeList
        this.model.etcSiteCodeList = value.etcSiteCodeList
        this.wasAllSelected = false
      }
    },
    approveStep (value) {
      if (value === 1) {
        this.model.updatePermission = this.userToApprove.updatePermission
        this.model.originalSiteCodeList = this.userToApprove.originalSiteCodeList
        this.model.etcSiteCodeList = this.userToApprove.etcSiteCodeList
      } else if (value === 3) {
        if (this.model.originalSiteCodeList.includes('ALL')) {
          this.model.etcSiteCodeList = []
          this.wasAllSelected = true
        } else {
          if (this.wasAllSelected) {
            this.model.etcSiteCodeList = this.userToApprove.etcSiteCodeList
          }
          this.wasAllSelected = false
        }
      }
    }
  },
  methods: {
    onSortOptionChange (columnName, isAsc) {
      this.userList.sort(function (a, b) {
        return String(a[columnName]).localeCompare(String(b[columnName]))
      })
      if (!isAsc) {
        this.userList.reverse()
      }
      this.sortOptions = {
        column: columnName,
        isAsc: isAsc
      }
    },
    onRejectConfirm (user) {
      this.userToReject = user
    },
    onRejectUser () {
      userApprove({
        approve: false,
        userNum: this.userToReject.userNum
      }).then(result => {
        this.userToReject = null
        this.load()
      })
    },
    onApproveConfirm (user) {
      user.originalSiteCodeList = user.siteCodeList
      this.userToApprove = user
      this.approveStep = 1
    },
    onApproveUserStep2 (updatePermission) {
      this.model.updatePermission = updatePermission
      this.approveStep = 2
      if (this.model.originalSiteCodeList.length) {
        this.canGoStep3 = true
      } else {
        this.canGoStep3 = false
      }
    },
    onApproveUser () {
      userApprove({
        approve: true,
        userNum: this.userToApprove.userNum,
        updatePermission: this.model.updatePermission,
        originalSiteCodeList: this.model.originalSiteCodeList,
        etcSiteCodeList: this.model.etcSiteCodeList
      }).then(result => {
        this.userToApprove = null
        this.load()
      })
    },
    onSiteCodeChange (value) {
      if (value.length) {
        this.canGoStep3 = true
      } else {
        this.canGoStep3 = false
      }
      this.model.originalSiteCodeList = value
    },
    onEtcSiteCodeChange (value) {
      this.model.etcSiteCodeList = value
    },
    load () {
      userList({
        active: false
      }).then(result => {
        this.userList = result
        this.userList.forEach(item => {
          item.isHide = false
          if (item.siteCodeList) {
            item.siteCodeList.sort()
          }
        })
        this.onSortOptionChange(this.sortOptions.column, this.sortOptions.isAsc)
      })

      getEtcSiteCodeList('ETC').then(result => {
        this.etcSiteCodeList[0].items = []
        result.forEach(item => {
          this.etcSiteCodeList[0].items.push(
            {
              value: item.siteCode,
              text: ''
            }
          )
        })
        this.allEtcSiteCodeList = []
        this.etcSiteCodeList[0].items.map(item => {
          this.allEtcSiteCodeList.push(item.value)
        })
      })
    },
    copyEmail (email) {
      navigator.clipboard.writeText(email)
        .then(() => {
          this.toastMessage = this.$t('common.copy')
          this.realToastMessage = this.$t('common.copy')

          if (this.toastTimer) {
            clearTimeout(this.toastTimer)
          }
          this.toastTimer = setTimeout(() => {
            this.toastMessage = null
            setTimeout(() => {
              this.realToastMessage = null
            }, 300)
          }, 3000)
        })
    }
  },
  created () {
    this.load()
  }
}
</script>

<style lang="scss" scoped="scoped">
.request_management__content {
  position: relative;
  float: left;
  width: calc(100% - 211px);
  margin-top: 40px;
  box-sizing: border-box;

  .content__header {
    font-size: 26px;
    font-weight: bold;
    color: #444444;
    margin-left: 30px;
    margin-right: 30px;
    padding-bottom: 22px;

    h2 {
      padding-left: 10px;
    }

    .input__search {
      position: absolute;
      right: 30px;
      top: -5px;

      .search-input {
        width: 240px;
        height: 38px;
        line-height: 36px;
        border-radius: 20px;
        border: solid 1px #bbbbbb;
        background-color: #ffffff;
        padding-left: 18px;
        padding-right: 26px;
        box-sizing: border-box;
        font-size: 14px;
        color: #2f2f2f;

        &:focus {
          border: solid 1px #289eef;
        }
      }

      .icon {
        position: absolute;
        right: 11px;
        transform: translateY(-50%);
        top: 18px;
      }
    }
  }

  .content__body {
    height: calc(100vh - 136px) !important;
    width: calc(100% - 60px) !important;
    margin: 1px 30px 0;
    overflow-y: auto;
    padding: 1px;

    & /deep/ .content-box {
      padding: 25px;
    }

    .load {
      height: 440px;
      border-bottom: 1px solid #dddddd;
      position: relative;

      .loading {
        @include positionXYcenter;
      }
    }

    table {
      font-size: 13px;
      table-layout: fixed;
      width: 100%;
    }

    table thead tr {
      height: 50px;
      background-color: #555555;
      line-height: 1.23;
      color: #ffffff;
      text-align: center;

      th:nth-of-type(1) {
        position: relative;
        &:before {
          content: "";
          display: inline-block;
          width: 15px;
          height: 50px;
          position: absolute;
          top: 0;
          left: -5px;
          background-color: #555;
          border-radius: 5px;
        }
      }
      th:nth-last-of-type(1) {
        position: relative;
        &:after {
          content: "";
          display: inline-block;
          width: 10px;
          height: 50px;
          position: absolute;
          top: 0;
          right: -5px;
          background-color: #555;
          border-radius: 5px;
        }
      }

      .align-left {
        text-align: left;
        padding-left: 10px;
      }

      .align-right {
        text-align: right;
        padding-right: 2px;
      }

      th.head-weekly {
        span {
          vertical-align: middle;
        }

        .not-support-weekly {
          position: relative;
          display: inline-block;
          vertical-align: middle;
          margin-left: 5px;

          .icon--info-white-size17 {
            width: 13px;
            height: 13px;
            background-size: contain;
          }
        }
      }

      span {
        position: relative;
        display: inline-block;
        .icon {
          position: absolute;
          top: 50%;
          right: -19px;
          transform: translateY(-50%);
        }
      }
    }

    table tbody tr {
      height: 50px;
      color: #2f2f2f;
      line-height: 1.23;
      font-size: 13px;
      text-align: center;
      border-bottom: 1px solid #ebebeb;
      box-sizing: border-box;

      &:not(.deleted):hover {
        background-color: #f4f4f4;
      }

      &.checked:not(.deleted) {
        border: solid 1px #289eef;
        outline: solid 3px rgba(40, 158, 239, 0.18);
      }

      .align-left {
        text-align: left;
        padding-left: 10px;
        box-sizing: border-box;
      }
      .align-right {
        text-align: right;
        padding-right: 15px;
        box-sizing: border-box;
      }

      td {
        padding: 10px 0;
      }

      .data__button {
        .ui-btn {
          margin-left: 5px;
        }
      }

      .no-request {
        color: #767676;
        height: 236px;
        background: #ffffff !important;
      }
    }
  }

  .no-result {
    @include positionYcenter;
    text-align: center;
    font-size: 14px;
    color: #767676;
    width: 100%;

    .title {
      margin: 15px 0;
      font-size: 20px;
      font-weight: 500;
      color: #2f2f2f;
    }
    .keyword {
      font-size: 15px;
      line-height: 24px;
      font-weight: 500;
      color: #ff8400;
      margin: 30px auto 0;
      width: 524px;
      word-break: break-all;
    }
  }

  .user_reject {
    padding: 50px 30px 25px 30px;
    text-align: center;

    .title {
      font-size: 20px;
      font-weight: bold;
      color: #444444;
      margin-top: 15px;
    }
    .desc {
      font-size: 14px;
      line-height: 1.43;
      color: #2f2f2f;
      margin-top: 12px;
      height: 50px;
      -ms-word-break: break-all;

      span {
        word-break: break-all;
      }
    }
    .ui-btn {
      margin: 30px 5px 0;
    }
  }

  .user_approve {
    color: #444444;

    .close {
      position: absolute;
      top: 12px;
      right: 12px;
    }

    h3 {
      font-size: 20px;
      padding: 30px;
      font-weight: bold;
    }

    .select {
      display: flex;
      margin-top: 70px;
      padding: 0 30px;
      height: 304px;

      .half {
        width: calc(50% - 1px);
        height: 100%;
        text-align: center;
        border-left: 1px solid #d8d8d8;

        &:first-child {
          border-right: 1px solid #d8d8d8;
          border-left: none;
        }

        .icon {
          width: 80px;
          height: 71px;
          margin: 15px 0;
        }

        .title {
          font-size: 20px;
          width: 216px;
          margin: 10px auto;
          font-weight: bold;
        }

        .desc {
          color: #2f2f2f;
          line-height: 1.54;
          font-size: 13px;
          height: 105px;
          width: 226px;
          margin: 0 auto;
        }

        .ui-btn {
          width: 156px;
        }
      }
    }

    .ui_checkselect {
      margin: 20px 30px;
    }

    .buttons {
      text-align: center;
      margin-top: 4px;

      .ui-btn {
        margin: 0 5px;
      }
    }
  }
  .layout-toast {
    z-index: 99999999;
    position: fixed;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    background-color: #999;
    color: #fff;
    margin-top: 13px;
    padding: 10px 13px;
    border-radius: 8px;
    box-shadow: 0 4px 7.5px 0 rgba(0, 0, 0, 0.16);

    &.fade-leave-active,
    &.fade-enter-active {
      transition: opacity 0.3s ease,
      transform 0.3s ease;
      transform: translateX(-50%);
    }

    &.fade-enter,
    &.fade-leave-to {
      transform: translateX(-50%) translateY(-10px);
    }
  }
}
</style>
